// Global
import React from 'react';

// Local
import { Themes } from 'lib/themes';
import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import {
  AspectRatioClassMap,
  ImageAspectRatio,
} from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import clsx from 'clsx';
import styles from './FeatureImageAndText.module.scss';
import { FeatureDetailCard } from './FeatureDetailCard/FeatureDetailCard';

export type FeatureCardAlignment = 'ContentLeft' | 'ContentRight' | 'ContentCenter';
export type FeatureImageSplit = 'OneThird' | 'OneHalf';

export interface FeatureImageAndTextProps {
  image: React.ReactNode;
  icon: React.ReactNode;
  cta1: React.ReactNode;
  cta2: React.ReactNode;
  headline: React.ReactNode;
  eyebrow: React.ReactNode;
  subheadline: React.ReactNode;
  description: React.ReactNode;
  theme?: Themes;
  contentPosition?: FeatureCardAlignment;
  imageAspectRatio?: ImageAspectRatio;
  imageSplit: FeatureImageSplit;
  noPadding: boolean;
  headlineId?: string;
  /** Defaults to 2 (native <h2>). Set to 3 when this instance is nested under a visible h2 on the page; renders as div[role=heading][aria-level=3] since the shared markup can't use a native <h3> without affecting other instances. */
  headlineLevel?: 2 | 3;
}

const FeatureCardPositionMap = {
  ContentLeft: 'content-left',
  ContentRight: 'content-right',
  ContentCenter: 'content-center',
} as const;

export const FeatureImageAndText = (props: FeatureImageAndTextProps) => {
  const contentPosition = FeatureCardPositionMap[props.contentPosition ?? 'ContentRight'];
  return (
    <BrandAndThemeProvider theme={props.theme}>
      <div
        id={props.headlineId}
        data-component="authorable/shared/content/feature/feature"
        tabIndex={-1}
      >
        <div className={clsx(styles.featureStandard, { [styles.noPadding]: props.noPadding })}>
          <div className={clsx(styles.content, styles[contentPosition])}>
            <div
              className={clsx(styles.copyContainer, {
                [styles.oneThird]: props.imageSplit === 'OneThird',
              })}
            >
              <FeatureDetailCard {...props} contentAlignment={'left'} />
            </div>

            <div className={styles.imageContainer}>
              <div
                className={clsx(
                  styles.image,
                  styles[`imageRadius_${contentPosition}`],
                  AspectRatioClassMap[props.imageAspectRatio ?? '1:1']
                )}
              >
                {props.image}
              </div>
            </div>
          </div>
        </div>
      </div>
    </BrandAndThemeProvider>
  );
};
